import {
	EmptyState,
	ListSkeleton,
	SectionCard,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@atlas/ui-components'
import { Badge, cn } from '@atlas/ui-primitives'
import type { ReactNode } from 'react'
import type { RuleResultOutcome, RuleResultProvenance, RuleValidationResultRow } from './rule-validation-results.types'

/** The card's default heading. Shared with a host's own skeleton so the two cannot diverge. */
export const RULE_VALIDATION_RESULTS_TITLE = 'Rule validation'

/**
 * Every outcome is stated, and the four are visually distinct.
 *
 * `needs_review` is a follow-up state, NOT a soft pass: it carries the warning tone and
 * its own wording, because the whole point of recording it is that nobody could tell. A
 * record with no verdict yet says so outright for the same reason.
 */
const OUTCOME: Record<RuleResultOutcome, { label: string; variant: 'success' | 'danger' | 'warning' | 'muted' }> = {
	pass: { label: 'Pass', variant: 'success' },
	fail: { label: 'Fail', variant: 'danger' },
	needs_review: { label: 'Needs review', variant: 'warning' },
	pending: { label: 'No verdict yet', variant: 'muted' },
}

const SEVERITY_LABEL = { blocking: 'Blocking', warning: 'Warning' } as const

/**
 * Blocking rules first, and within a severity the outcomes that need someone before the
 * ones that do not. A reader scanning an audit report is looking for what went wrong.
 */
const OUTCOME_RANK: Record<RuleResultOutcome, number> = { fail: 0, needs_review: 1, pending: 2, pass: 3 }
const SEVERITY_RANK = { blocking: 0, warning: 1 } as const

const byPriority = (a: RuleValidationResultRow, b: RuleValidationResultRow) =>
	SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity] || OUTCOME_RANK[a.outcome] - OUTCOME_RANK[b.outcome]

export interface RuleValidationResultsProps {
	rows: RuleValidationResultRow[]
	/** When the run was requested, ISO. Rendered in the header beside the aggregate. */
	runDate?: string | null
	/** Stated in the header while the run is still answering, e.g. `Running`. */
	runStatus?: ReactNode
	/**
	 * Whether the run has finished. Only a finished run can be reported as having resolved
	 * zero rules - saying that of a run still fanning out states a result it has not reached.
	 */
	isRunComplete?: boolean
	/** Whether the first read is still in flight. Nothing below the header is known yet. */
	isLoading?: boolean
	/**
	 * Whether the read failed. Distinct from an empty result: "nothing was recorded" is a
	 * claim about the content, and making it for a read that never landed tells a reviewer
	 * this content was never validated when the truth is that nobody could say.
	 */
	isError?: boolean
	title?: ReactNode
	/** Right-aligned affordance - the validate action on the surfaces that offer one. */
	action?: ReactNode
	/** Render a subject header row above each subject's rules. For a target holding many assets. */
	groupBySubject?: boolean
	className?: string
}

/**
 * The readable form of a validation run: one row per rule per subject, with the verdict,
 * why, and what it rests on.
 *
 * It is dated advisory evidence, not a compliance verdict - nothing gates on it - so the
 * header states WHEN the run happened as prominently as what it found. A row's wording is
 * the rule as snapshotted on the record, so a report stays readable after the rule is
 * edited.
 */
export function RuleValidationResults({
	rows,
	runDate,
	runStatus,
	isRunComplete = false,
	isLoading = false,
	isError = false,
	title = RULE_VALIDATION_RESULTS_TITLE,
	action,
	groupBySubject = false,
	className,
}: RuleValidationResultsProps) {
	const ordered = [...rows].sort(byPriority)

	return (
		<SectionCard
			title={title}
			count={isLoading ? undefined : rows.length || undefined}
			action={action}
			description={
				isLoading ? 'Reading the latest run…' : <RunSummary rows={rows} runDate={runDate} runStatus={runStatus} />
			}
			className={className}
		>
			{isLoading ? (
				<ListSkeleton rows={4} label="Loading validation results" />
			) : ordered.length === 0 ? (
				<EmptyBody runDate={runDate} isRunComplete={isRunComplete} isError={isError} />
			) : (
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead>Rule</TableHead>
							<TableHead>Subject</TableHead>
							<TableHead>Verdict</TableHead>
							<TableHead>Why</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>{groupBySubject ? groupedRows(ordered) : ordered.map(resultRow)}</TableBody>
				</Table>
			)}
		</SectionCard>
	)
}

/**
 * What an empty table means, which is four different things.
 *
 * The order matters: a failed read is never reported as an absence of findings, and only a
 * FINISHED run is reported as having resolved zero rules. A run still answering has recorded
 * nothing YET, which is not the same statement.
 */
function EmptyBody({
	runDate,
	isRunComplete,
	isError,
}: {
	runDate?: string | null
	isRunComplete: boolean
	isError: boolean
}): ReactNode {
	if (isError)
		return (
			<p className="text-ds-sm leading-5 text-ds-text-muted">
				Could not load the validation history for this content. Please retry.
			</p>
		)
	if (!runDate)
		return (
			<EmptyState
				variant="first-run"
				title="No run recorded"
				description="Request a validation to record a dated, per-rule audit of this content."
			/>
		)
	if (!isRunComplete)
		return (
			<EmptyState
				variant="first-run"
				title="No verdicts recorded yet"
				description="The latest run has not recorded any per-rule results. The header states where it has got to."
			/>
		)
	return (
		<EmptyState
			variant="filtered"
			title="This run recorded no rules"
			description="The latest run resolved zero applicable rules for this content."
		/>
	)
}

/** Subject header rows above each subject's rules, so a many-asset run reads per asset. */
function groupedRows(ordered: RuleValidationResultRow[]): ReactNode[] {
	const subjects = [...new Set(ordered.map((row) => row.subject))]
	return subjects.flatMap((subject) => [
		<TableRow key={`group-${subject}`} className="bg-ds-surface/60">
			<TableCell colSpan={4} className="font-medium text-ds-text text-ds-sm">
				{subject}
			</TableCell>
		</TableRow>,
		...ordered.filter((row) => row.subject === subject).map(resultRow),
	])
}

function resultRow(row: RuleValidationResultRow): ReactNode {
	const outcome = OUTCOME[row.outcome]
	return (
		<TableRow key={row.id} className="align-top">
			<TableCell>
				<span className="block text-ds-text">{row.statement}</span>
				<span className="mt-1 block font-mono text-ds-3xs text-ds-text-subtle uppercase tracking-wide">
					{SEVERITY_LABEL[row.severity]}
				</span>
			</TableCell>
			<TableCell>
				<span className="block text-ds-text">{row.subject}</span>
				<span className="mt-1 block text-ds-text-subtle text-ds-xs">{row.elementType}</span>
			</TableCell>
			<TableCell>
				<Badge variant={outcome.variant} size="sm">
					{outcome.label}
				</Badge>
				<span className="mt-1 block text-ds-text-subtle text-ds-xs">
					{row.evaluatedAt ? formatDate(row.evaluatedAt) : 'Not evaluated'}
				</span>
			</TableCell>
			<TableCell>
				<span className={cn('block', row.rationale ? 'text-ds-text' : 'text-ds-text-subtle italic')}>
					{row.rationale ?? 'No rationale recorded.'}
				</span>
				{row.evidence ? (
					<span className="mt-1 block text-ds-text-muted text-ds-xs">Evidence: {row.evidence}</span>
				) : null}
				<span className="mt-1 block text-ds-text-subtle text-ds-3xs uppercase tracking-wide">
					{provenanceLine(row.provenance)}
				</span>
			</TableCell>
		</TableRow>
	)
}

/**
 * How the verdict was produced, never blank.
 *
 * A deterministic check has no model to name, and printing an empty model value there
 * would read as a missing one - the reader cannot tell "no model was involved" from
 * "we lost the record of which model was". A record whose check never ran states that
 * plainly instead of borrowing the other kind's label.
 */
export function provenanceLine(provenance: RuleResultProvenance): string {
	switch (provenance.kind) {
		case 'model':
			return `${provenance.model} · prompt ${provenance.promptVersion}`
		case 'deterministic':
			return `Deterministic check · ${provenance.field} ${provenance.operator}`
		case 'not_evaluated':
			return provenance.method === 'model'
				? 'Model check · not yet evaluated'
				: 'Deterministic check · not yet evaluated'
	}
}

function RunSummary({
	rows,
	runDate,
	runStatus,
}: Pick<RuleValidationResultsProps, 'rows' | 'runDate' | 'runStatus'>): ReactNode {
	if (rows.length === 0 && !runDate) return 'Advisory only — a run records evidence, it does not gate anything.'

	const count = (outcome: RuleResultOutcome) => rows.filter((row) => row.outcome === outcome).length
	// No records, no tally: "0 passed, 0 failed" beside a run that has not answered yet reads
	// as a finished result, and the empty state below already says what an empty table means.
	const tally = rows.length
		? [
				`${count('pass')} passed`,
				`${count('fail')} failed`,
				`${count('needs_review')} need review`,
				count('pending') ? `${count('pending')} awaiting a verdict` : null,
			]
		: []
	const parts = [runDate ? formatDate(runDate) : null, runStatus, ...tally].filter(Boolean)

	return <>{parts.map((part, index) => (index === 0 ? part : <span key={index}> · {part}</span>))}</>
}

const formatDate = (iso: string) => new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
